/**
 * TrackFader.tsx
 * =============================================================================
 * Professional vertical fader component with side-mounted LED meter.
 * Compact design inspired by Pro Tools and professional audio consoles.
 * 
 * @author Neuro-Audio Lab
 */

import { useCallback, useMemo } from 'react';
import type { TrackId } from '../../core/types';

// =============================================================================
// TYPES
// =============================================================================

interface TrackFaderProps {
  trackId: TrackId | 'master';
  label: string;
  volume: number;
  onChange: (trackId: TrackId | 'master', volume: number) => void;
  isActive?: boolean;
  icon?: React.ReactNode;
  color?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function TrackFader({
  trackId,
  label,
  volume,
  onChange,
  isActive = true,
  icon,
  color = '#D9052E',
}: TrackFaderProps) {
  // Convert 0-1 to percentage for display
  const volumePercent = Math.round(volume * 100);
  
  // Generate LED segments (12 segments for better resolution)
  // Fills from BOTTOM to TOP (DAW standard)
  const ledSegments = useMemo(() => {
    const segments = [];
    const activeSegments = Math.ceil(volume * 12);

    // Generate segments 1-12 (bottom to top)
    for (let i = 1; i <= 12; i++) {
      const isLit = i <= activeSegments && isActive;
      // Color gradient: green (bottom) -> yellow -> orange -> red (top)
      let segmentColor = '#22C55E'; // Green (0-60%)
      if (i > 10) segmentColor = '#EF4444'; // Red (85-100%)
      else if (i > 8) segmentColor = '#F97316'; // Orange (70-85%)
      else if (i > 6) segmentColor = '#F59E0B'; // Yellow (60-70%)

      segments.push({
        id: i,
        isLit,
        color: segmentColor,
      });
    }
    return segments;
  }, [volume, isActive]);
  
  // Handle slider change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newVolume = parseFloat(e.target.value);
      onChange(trackId, newVolume);
    },
    [trackId, onChange]
  );
  
  // Handle double-click to reset to default
  const handleDoubleClick = useCallback(() => {
    onChange(trackId, 0.7); // Reset to 70%
  }, [trackId, onChange]);

  return (
    <div className="flex flex-col items-center gap-2 select-none">
      {/* Track Label & Icon */}
      <div className="flex flex-col items-center gap-1 mb-1">
        {icon && (
          <span className="text-base opacity-70">{icon}</span>
        )}
        <span className="text-[10px] font-semibold text-neutral-300 uppercase tracking-widest">
          {label}
        </span>
      </div>
      
      {/* Main Fader Container - Horizontal Layout */}
      <div className="flex flex-row items-stretch gap-2">
        {/* Fader Track Container */}
        <div className="relative flex flex-col items-center">
          {/* Fader Track Background */}
          <div 
            className="relative w-7 h-32 rounded overflow-hidden"
            style={{ 
              background: 'linear-gradient(to bottom, rgba(30,30,30,0.8), rgba(20,20,20,0.9))',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)',
            }}
          >
            {/* Fill Level Indicator */}
            <div
              className="absolute bottom-0 left-0 right-0 transition-all duration-75 ease-out pointer-events-none"
              style={{
                height: `${volumePercent}%`,
                background: isActive
                  ? `linear-gradient(to top, ${color}95, ${color}30)`
                  : 'rgba(100,100,100,0.2)',
                boxShadow: isActive 
                  ? `0 0 8px ${color}40, inset 0 -1px 2px ${color}60`
                  : 'none',
              }}
            />
            
            {/* Scale Markings */}
            <div className="absolute inset-0 flex flex-col justify-between py-1.5 pointer-events-none">
              {[...Array(9)].map((_, i) => {
                const isMainMark = i % 2 === 0;
                return (
                  <div
                    key={i}
                    className={`h-px ${isMainMark ? 'bg-white/25' : 'bg-white/10'}`}
                    style={{ 
                      width: isMainMark ? '50%' : '30%',
                      marginLeft: 'auto',
                      marginRight: '2px',
                    }}
                  />
                );
              })}
            </div>
            
            {/* Fader Knob */}
            <div
              className="absolute left-1/2 -translate-x-1/2 w-7 h-5 rounded-sm transition-all duration-75 cursor-ns-resize"
              style={{
                bottom: `calc(${volumePercent}% - 10px)`,
                background: 'linear-gradient(to bottom, #4a4a4a 0%, #353535 50%, #2a2a2a 100%)',
                boxShadow: `
                  0 1px 3px rgba(0,0,0,0.8),
                  inset 0 1px 0 rgba(255,255,255,0.15),
                  inset 0 -1px 0 rgba(0,0,0,0.3),
                  0 0 0 1px rgba(0,0,0,0.4)
                `,
              }}
            >
              {/* Knob Grip Pattern */}
              <div className="absolute inset-x-1 top-1/2 -translate-y-1/2 flex flex-col gap-[2px]">
                <div className="h-px bg-white/15" />
                <div className="h-px bg-white/20" />
                <div className="h-px bg-white/15" />
              </div>
              
              {/* Knob Highlight */}
              <div 
                className="absolute top-0 left-0 right-0 h-[2px] rounded-t-sm"
                style={{
                  background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.25), transparent)',
                }}
              />
            </div>
            
            {/* Hidden Range Input (for accessibility and control) */}
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleChange}
              onDoubleClick={handleDoubleClick}
              className="absolute inset-0 w-full h-full opacity-0 cursor-ns-resize"
              style={{
                writingMode: 'vertical-lr',
                direction: 'rtl',
              }}
              aria-label={`${label} volume`}
            />
          </div>
        </div>
        
        {/* LED Meter - Vertical, Side-Mounted */}
        <div className="flex flex-col-reverse gap-[3px] justify-end h-32 py-1.5">
          {ledSegments.map((segment) => (
            <div
              key={segment.id}
              className="w-1.5 h-[8px] rounded-[1px] transition-all duration-50"
              style={{
                backgroundColor: segment.isLit 
                  ? segment.color 
                  : 'rgba(80,80,80,0.3)',
                boxShadow: segment.isLit 
                  ? `0 0 4px ${segment.color}80, inset 0 0 2px ${segment.color}` 
                  : 'inset 0 1px 1px rgba(0,0,0,0.5)',
                border: segment.isLit 
                  ? 'none'
                  : '0.5px solid rgba(60,60,60,0.5)',
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Volume Value Display */}
      <div 
        className="px-2 py-0.5 rounded-sm text-[10px] font-mono font-semibold tabular-nums tracking-wider"
        style={{ 
          backgroundColor: 'rgba(30,30,30,0.7)',
          color: isActive ? color : '#888',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.3)',
        }}
      >
        {volumePercent}%
      </div>
    </div>
  );
}

export default TrackFader;